import React, { useState, useRef, useEffect } from "react";
import { Copy, CopyCheck, ImagePlus, Trash2, Upload, X } from "lucide-react";
import { toast } from "react-toastify";
import { getDecryptedUserFromCookie } from "../utils/getCookie";

const ImageBox = ({
  selectedImage = null,
  mode = "single",
  selectedImages = [],
  onSelect,
  onDelete,
}) => {
  const images = Array.isArray(selectedImages) && selectedImages.length
    ? selectedImages
    : selectedImage
      ? [selectedImage]
      : [];
  const [localSelected, setLocalSelected] = useState(images);
  const [mediaImages, setMediaImages] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("media");
  const [showTooltip, setShowTooltip] = useState(false);
  const fileInputRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const url = process.env.REACT_APP_LINK;
  const user = getDecryptedUserFromCookie();

  const imagePath = localSelected[0]?.image || localSelected[0]?.url || "";
  const imageLink = imagePath.startsWith("http")
    ? imagePath
    : `${url}${imagePath}`;


  const copyToClipboard = () => {
    navigator.clipboard.writeText(imageLink);
    setCopied(true);

    setTimeout(() => setCopied(false), 1500);
  };

  // ✅ Fetch all gallery images
  const fetchGalleries = async () => {
    try {
      const res = await fetch(`${url}/api/images/${user.main_id}`);
      if (!res.ok) throw new Error("Failed to fetch gallery items");
      const json = await res.json();
      console.log(json)
      setMediaImages(json);
    } catch (err) {
      console.error("❌ Error fetching galleries:", err);
    }
  };

  // ✅ Handle upload
  const handleUpload = async (files) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("image", file));
    formData.append("user_id", user.main_id)
    formData.append("user_name", user.full_name)

    try {
      const response = await fetch(`${url}/api/images/upload`, {
        method: "POST",
        body: formData, // ❗ Do not set Content-Type manually
      });


      if (!response.ok) throw new Error("Failed to upload");
      toast.success("Image(s) uploaded successfully 🎉");
      await fetchGalleries();
      setActiveTab("media");
    } catch (err) {
      console.error("❌ Upload error:", err);
      toast.error("Upload failed. Please try again.");
    }
  };

  const handleFileChange = (e) => handleUpload(e.target.files);
  const handleDrop = (e) => {
    e.preventDefault();
    handleUpload(e.dataTransfer.files);
  };

  const handleDeleteImage = async (e, imgObj) => {
    e.stopPropagation();
    if (!window.confirm("Delete this image permanently?")) return;

    try {
      const res = await fetch(`${url}/api/images/${imgObj.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete");
      toast.success("Image deleted successfully 🎉");
      await fetchGalleries(); // refresh list
    } catch (err) {
      console.error("❌ Delete error:", err);
      toast.error("Failed to delete image");
    }
  };


  const handleSelectImage = (imgObj) => {
    // Append to the array (multi-select)
    const newArr = [...localSelected, imgObj];
    setLocalSelected(newArr);
    onSelect?.(newArr); // send whole array back to parent
  };

  // const toggleSelect = (imgObj) => {
  //   const key = imgObj.image || imgObj.url; // unique identifier
  //   const already = localSelected.some((s) => (s.image || s.url) === key);

  //   let newArr;
  //   if (already) {
  //     // ---- REMOVE ----
  //     newArr = localSelected.filter((s) => (s.image || s.url) !== key);
  //   } else {
  //     // ---- ADD ----
  //     newArr = [...localSelected, imgObj];
  //   }

  //   setLocalSelected(newArr);
  //   // Notify parent – keep the same shape it expects
  //   if (selectedImage !== null) {
  //     // single-image mode → send the only image or null
  //     onSelect?.(newArr[0] ?? null);
  //   } else {
  //     // multi-image mode → send the whole array
  //     onSelect?.(newArr);
  //   }
  // };

  const toggleSelect = (imgObj) => {
    const isSingleMode = mode === "single";
    console.log(imgObj)
    if (isSingleMode) {
      // SINGLE IMAGE MODE
      setLocalSelected([imgObj]);
      onSelect?.(imgObj);
      return;
    }

    // MULTI IMAGE MODE
    const key = imgObj?.image || imgObj?.url;
    const already = localSelected.some((s) => (s?.image || s?.url) === key);

    const newArr = already
      ? localSelected.filter((s) => (s?.image || s?.url) !== key)
      : [...localSelected, imgObj];

    setLocalSelected(newArr);
    onSelect?.(newArr);
  };




  const handleDelete = (indexToRemove) => {
    const newArr = localSelected.filter((_, i) => i !== indexToRemove);
    setLocalSelected(newArr);
    onDelete?.(indexToRemove); // optional index for parent to know which one
  };

  const clearAll = () => {
    setLocalSelected([]);
    onDelete?.(); // parent can clear everything
  };

  useEffect(() => {
    fetchGalleries();
  }, []);

  useEffect(() => {
    let newImages = [];

    if (mode === "single") {
      newImages = selectedImage ? [selectedImage] : [];
    } else {
      newImages = Array.isArray(selectedImages) ? selectedImages : [];
    }

    const isSame =
      newImages.length === localSelected.length &&
      newImages.every(
        (img, i) =>
          (img?.image || img?.url) ===
          (localSelected[i]?.image || localSelected[i]?.url)
      );

    if (!isSame) setLocalSelected(newImages);
  }, [selectedImage, selectedImages, mode]);

  console.log(localSelected)

  return (
    <>
      {/* Image preview box */}
      <div
        className="h-72 min-w-72 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center cursor-pointer overflow-hidden relative"
        onClick={() => setOpen(true)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* ONE IMAGE → BIG PREVIEW */}
        {localSelected.length === 1 && (
          <img
            src={`${url}${localSelected[0]?.url}`}
            alt="Preview"
            className="w-full h-full object-cover"
          />
        )}

        {/* MULTIPLE IMAGES → HORIZONTAL SCROLL */}
        {localSelected.length > 1 && (
          <div className="flex gap-2 p-2 h-full overflow-x-auto scrollbar-thin">
            {localSelected.map((img, idx) => (
              <div
                key={idx}
                className="flex-shrink-0 rounded overflow-hidden border border-gray-300"
              >
                <img
                  src={`${url}${img?.url}`}
                  alt={`thumb-${idx}`}
                  className="w-72 h-72 object-cover"
                />
              </div>
            ))}
          </div>
        )}

        {/* NO IMAGE */}
        {localSelected.length === 0 && <ImagePlus className="w-10 h-10 text-gray-400" />}

        {/* Tooltip actions */}
        {showTooltip && localSelected.length > 0 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-2 bg-white shadow-lg rounded-md px-2 py-1">
            <button
              className="text-blue-600 text-sm hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
            >
              {localSelected.length > 1 ? "Edit" : "Replace"}
            </button>
            <button
              className="text-red-500 text-sm hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
            >
              {localSelected.length > 1 ? "Delete all" : "Delete"}
            </button>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[100]">
          <div className="bg-white rounded-lg max-w-5xl w-[98%] max-h-[95%] overflow-y-auto p-5 relative">
            <h2 className="text-lg font-semibold mb-4">Select or Upload Image</h2>
            <button
              className="absolute top-3 right-3 text-gray-500 text-xl hover:text-black"
              onClick={() => setOpen(false)}
            >
              <X />
            </button>

            {/* TABS */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                className={`px-4 py-2 ${activeTab === "media" ? "border-b-2 border-blue-500 font-medium" : "text-gray-500"
                  }`}
                onClick={() => setActiveTab("media")}
              >
                Media
              </button>
              <button
                className={`px-4 py-2 ${activeTab === "upload" ? "border-b-2 border-blue-500 font-medium" : "text-gray-500"
                  }`}
                onClick={() => setActiveTab("upload")}
              >
                Upload
              </button>
            </div>
            {/* {console.log(localSelected)} */}
            {/* MEDIA TAB */}
            {activeTab === "media" && (
              <div className="flex items-start max-h-[80vh] gap-4">
                {/* LEFT PREVIEW (shows first selected or nothing) */}
                <div className="flex-1 min-w-[250px] max-w-[300px] flex flex-col border border-gray-200 rounded-lg p-4 overflow-hidden">
                  {localSelected.length > 0 ? (
                    <>
                      <div className="flex items-center justify-center mb-4">
                        <img
                          src={`${url}${localSelected[0]?.url}`}
                          alt="Preview"
                          className="h-[200px] w-[200px] object-contain border border-gray-200 rounded"
                        />
                      </div>
                      <div className="text-sm text-gray-600 space-y-1 mb-4 border-t border-gray-200 pt-2">
                        <p>
                          <strong>File Name:</strong> {localSelected[0].title || "N/A"}
                        </p>
                        <p>
                          <strong>Type:</strong> {localSelected[0].type || "N/A"}
                        </p>
                        <p className="flex items-center">
                          <strong>URL:</strong>{" "}
                          <a
                            href={imageLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 underline pl-0.5"
                          >
                            {/* {imageLink || "N/A"} */}
                          </a>
                          {imageLink && (
                            <button
                              onClick={copyToClipboard}
                              className="p-1 transition text-black ml-1"
                              title="Copy Link"
                            >
                              {copied ? <CopyCheck size={16} /> : <Copy size={16} />}
                            </button>
                          )}
                        </p>
                      </div>
                      <button
                        className="w-full py-2 px-4 rounded-full text-white font-semibold bg-gradient-to-r from-blue-500 to-blue-800 hover:from-blue-600 hover:to-blue-900 transition-colors"
                        onClick={() => setOpen(false)}
                      >
                        Use Image{localSelected.length > 1 ? "s" : ""}
                      </button>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-400">
                      No image selected
                    </div>
                  )}
                </div>

                {/* THUMBNAILS GRID */}
                <div className="flex-[2] w-full overflow-y-auto flex flex-wrap gap-3 border border-gray-200 rounded-lg p-4">
                  {mediaImages.length === 0 ? (
                    <p className="text-center text-gray-500 w-full">No images uploaded yet.</p>
                  ) : (
                    mediaImages.map((imgObj, i) => {
                      const key = imgObj.image || imgObj.url;
                      const isSelected = localSelected.some(
                        (s) => (s?.image || s?.url) === key
                      );
                      return (
                        <div
                          key={i}
                          className={`relative group border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${isSelected
                            ? "border-blue-500 ring-2 ring-blue-300"
                            : "border-gray-200"
                            }`}
                          onClick={() => toggleSelect(imgObj)}
                        >
                          <img
                            src={`${url}${imgObj.url}`}
                            alt={imgObj.title}
                            className="w-[120px] h-[120px] object-cover"
                          />
                          {isSelected && (
                            <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                              <svg
                                className="w-8 h-8 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          )}
                          <button
                            onClick={(e) => handleDeleteImage(e, imgObj)}
                            className="cursor-pointer absolute top-0.5 right-0.5 text-red-500 z-[101] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md bg-red-50 hover:bg-red-100 w-8 h-8 flex justify-center items-center"
                            title="Delete image"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* UPLOAD TAB */}
            {activeTab === "upload" && (
              <div
                className="border-2 border-dashed rounded-xl p-10 text-center cursor-pointer"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <Upload className="mx-auto w-10 h-10 text-gray-400 mb-2" />
                <p className="text-gray-500 mb-2">Drag & drop image(s) here</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current.click()}
                  className="inline-block py-2 px-4 rounded-full text-white font-semibold bg-gradient-to-r from-blue-500 to-blue-800 hover:from-blue-600 hover:to-blue-900 transition-colors"
                >
                  Browse
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageBox;
